import { useCallback, useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import { ALTERNATE_DATE_FORMAT, formatDateToString, HLSPlayer, PlayerState, useLoadedSound, usePosition } from '@bpm-web-app/utils';
import { ContestSubmissionSongUrl, ContestSubmissionSongWave } from '@bpm-web-app/create-api-sdk';
import { useRouter } from 'next/router';
import { useContestReportStreamSubmission } from '@bpm-web-app/swr-hooks';
import styles from './submission.module.css';
import { useSession } from '../shared/session-provider/session-provider';
import SmallPlay from '../../assets/icons/create/small-play.svg';
import SmallHeart from '../../assets/icons/create/small-heart.svg';
import PauseIcon from '../../assets/icons/player-pause.svg';
import useContest from '../shared/three-dots-sheet/useContest';
import { useCreatePlayer } from '../../../../utils/src/lib/create-player.context';
import { CreateWave } from '../create-wave/create-wave';
import { CustomIcon } from '../shared/custom-icon/custom-icon';
import { ShareURL } from '../shared/share-url/share-url';
import TrackListCreateArtwork from '../shared/track-list/create/track-list-create-artwork';

export interface SubmissionProps {
    contestArtworkUrl: string;
    contestId: string;
    contestTitle: string;
    id: string;
    isLastItem?: boolean;
    user: {
        id: number;
        username: string;
        full_name: string;
        profile_image_thumbnail_url?: string;
    };
    created_at: string;
    description: string;
    streamCount: number;
    likeCount: number;
    title: string;
    liked: boolean;
    songUrl: ContestSubmissionSongUrl;
    songWave: ContestSubmissionSongWave;
    mutate: () => void;
}

export function Submission({
    contestArtworkUrl,
    contestId,
    contestTitle,
    id,
    isLastItem,
    user,
    created_at,
    description,
    streamCount,
    likeCount,
    title,
    liked,
    songUrl,
    songWave,
    mutate,
}: SubmissionProps) {
    const { user: loggedUser } = useSession();

    const { editContestSubmission, likeContestSubmission, unlikeContestSubmission } = useContest(contestId, false);

    const router = useRouter();
    const { submissionId } = router.query;
    const { top, ref: submissionRef } = usePosition();

    const contestReport = useContestReportStreamSubmission();

    useEffect(() => {
        if (submissionId && id === submissionId && top !== undefined) {
            window.scrollTo({
                top: top - 50,
                behavior: 'smooth',
            });
        }
    }, [submissionId, top, id]);

    const [elapsed, setElapsed] = useState(0);
    const [duration, setDuration] = useState(0);

    const [isEditing, setIsEditing] = useState(false);

    const canEdit = useMemo(() => user?.id === loggedUser?.id, [loggedUser?.id, user?.id]);

    const [editedDescription, setEditedDescription] = useState(description);
    const [editedTitle, setEditedTitle] = useState(title);

    const isSoundLoaded = useLoadedSound();

    const { closePlayer, setCurrentSoundId, currentSoundId } = useCreatePlayer();

    const setElapsedTime = useCallback(
        (time: number) => {
            if (currentSoundId === id && time > 0) {
                setElapsed(time);
            }
        },
        [currentSoundId, id]
    );

    useEffect(() => {
        PlayerState.onProgress(setElapsedTime);

        return () => {
            PlayerState.offProgress(setElapsedTime);
        };
    }, [setElapsedTime]);

    useEffect(() => {
        if (currentSoundId === id && elapsed > 0 && duration === 0) {
            const hlsDuration = HLSPlayer.getDuration();
            if (hlsDuration > 0) setDuration(hlsDuration);
        }
    }, [elapsed, currentSoundId, id, duration]);

    const playSound = useCallback(
        async (progress?: number) => {
            contestReport(id);
            mutate();

            if (duration === 0) {
                setDuration(HLSPlayer.getDuration());
            }

            const finalProgress = progress || elapsed;

            if (finalProgress > 0) {
                HLSPlayer.goTo(finalProgress);
            }

            await HLSPlayer.play();
        },
        [contestReport, duration, elapsed, id, mutate]
    );

    const loadSound = useCallback(async () => {
        if (songUrl.hls) {
            const lastLoadedSoundUrl = HLSPlayer.getLastLoadedSound();
            if (lastLoadedSoundUrl === songUrl.hls) {
                playSound();
                return;
            }
            await HLSPlayer.load(songUrl.hls);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentSoundId === id && isSoundLoaded) {
            playSound();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSoundLoaded]);

    useEffect(() => {
        if (currentSoundId === id) {
            loadSound();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSoundId, id]);

    const handlePlay = useCallback(async () => {
        closePlayer();
        setCurrentSoundId(id);
    }, [closePlayer, id, setCurrentSoundId]);

    const handleSeek = useCallback(
        async (progress: number) => {
            try {
                closePlayer();

                if (currentSoundId !== id) {
                    handlePlay();
                } else {
                    playSound(progress);
                }
            } catch {
                // Placeholder
            }
        },
        [closePlayer, currentSoundId, handlePlay, id, playSound]
    );

    const handleStop = useCallback(() => {
        closePlayer();
        HLSPlayer.pause();
        setCurrentSoundId(undefined);
    }, [closePlayer, setCurrentSoundId]);

    const updateSubmission = useCallback(() => {
        setIsEditing(false);
        editContestSubmission(id, editedTitle, editedDescription);
    }, [editContestSubmission, editedDescription, editedTitle, id]);

    const cancelEditing = useCallback(() => {
        setIsEditing(false);
        setEditedDescription(description);
        setEditedTitle(title);
    }, [description, title]);

    const handleLike = useCallback(() => {
        if (liked) {
            unlikeContestSubmission(id);
            return;
        }

        likeContestSubmission(id);
    }, [id, likeContestSubmission, liked, unlikeContestSubmission]);

    return (
        // eslint-disable-next-line @typescript-eslint/dot-notation
        <div ref={submissionRef} id={id} className={styles['submission']}>
            <div className={classNames(styles['submission__column'], styles['submission__column--image'])}>
                <TrackListCreateArtwork artworkURL={contestArtworkUrl} hintText={contestTitle} isLastItem={isLastItem} />
            </div>

            {/* play */}
            <div className={classNames(styles['submission__column'], styles['submission__column--play'])}>
                <button
                    type="button"
                    className={styles['submission__play-btn']}
                    aria-label="play/pause submission"
                    onClick={currentSoundId === id ? handleStop : handlePlay}
                >
                    {currentSoundId === id ? <PauseIcon /> : <SmallPlay />}
                </button>
            </div>

            {/* wave */}
            <div className={classNames(styles['submission__column'], styles['submission__column--wave'], {
                [styles['submission__column--wave-hidden']]: currentSoundId !== id || elapsed === 0,
            })}>
                <CreateWave wave={songWave} handleSeek={handleSeek} elapsed={elapsed} duration={`${duration}`} isPaused={currentSoundId !== id} />
            </div>

            {/* title */}
            <div className={classNames(styles['submission__column'], styles['submission__column--title'])}>
                {isEditing ? (
                    <>
                        <textarea
                            className={classNames(styles['submission__content-input'], styles['submission__content-input--title'])}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            value={editedTitle}
                        />
                        <div className={styles['submission__edit-actions']}>
                            <button
                                className={classNames(styles['submission__content-submit'], styles['submission__content-submit--primary'])}
                                aria-label="update submission"
                                type="button"
                                onClick={updateSubmission}
                            >
                                Save
                            </button>
                            <button
                                className={classNames(styles['submission__content-submit'], styles['submission__content-submit--secondary'])}
                                aria-label="cancel update submission"
                                onClick={cancelEditing}
                                type="button"
                            >
                                Cancel
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles['submission__sound-title']}>{title}</div>
                        <div className={styles['submission__sound-artist']}>{description || user?.full_name}</div>
                    </>
                )}
            </div>

            {/* bpm slot — stream count */}
            <div className={classNames(styles['submission__column'], styles['submission__column--bpm'])}>
                <span className={styles['submission__meta']}>
                    <span className={classNames(styles['submission__meta-icon'], styles['submission__meta-icon--play'])}>
                        <SmallPlay />
                    </span>
                    <span className={styles['submission__meta-value']}>{streamCount}</span>
                </span>
            </div>

            {/* key slot — like count */}
            <div className={classNames(styles['submission__column'], styles['submission__column--key'])}>
                <span className={styles['submission__meta']}>
                    <span className={styles['submission__meta-icon']}>
                        <SmallHeart />
                    </span>
                    <span className={styles['submission__meta-value']}>{likeCount}</span>
                </span>
            </div>

            {/* time slot — submission date */}
            <div className={classNames(styles['submission__column'], styles['submission__column--time'])}>
                <span className={styles['submission__meta-value']}>{formatDateToString(created_at, ALTERNATE_DATE_FORMAT)}</span>
            </div>

            {/* actions */}
            <div className={classNames(styles['submission__column'], styles['submission__column--actions'])}>
                {!isEditing && canEdit && (
                    <CustomIcon
                        type="pencil-outline"
                        hasIconHover
                        hasBackgroundHover
                        container="round"
                        containerSize="regular"
                        tooltip="Edit"
                        onClick={() => setIsEditing(true)}
                    />
                )}
                <CustomIcon
                    type={liked ? 'heart-filled-icon' : 'heart-outline-icon'}
                    hasIconHover
                    hasBackgroundHover
                    container="round"
                    containerSize="regular"
                    tooltip={liked ? 'Unlike' : 'Like'}
                    onClick={handleLike}
                />
                <ShareURL currentUrl customTooltip="Copy Link" />
            </div>
        </div>
    );
}

export default Submission;
